import React, { createContext, useContext, useState, useEffect } from 'react';

const SessionSettingsContext = createContext();

export const useSessionSettings = () => {
    const context = useContext(SessionSettingsContext);
    if (!context) {
        throw new Error('useSessionSettings must be used within SessionSettingsProvider');
    }
    return context;
};

export const SessionSettingsProvider = ({ children }) => {
    // Initialize from localStorage or use defaults
    const getInitialSettings = () => {
        try {
            const stored = localStorage.getItem('sessionSettings');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Error reading sessionSettings from localStorage:', error);
        }
        return {
            productionGoal: 5,
            observationPeriod: 30,
            goalAmount: 100000,
            forecastAmount: 98000,
            workingDaysTotal: 22,
            workingDaysRemaining: 22
        };
    };

    const initialSettings = getInitialSettings();

    const [productionGoal, setProductionGoal] = useState(initialSettings.productionGoal);
    const [observationPeriod, setObservationPeriod] = useState(initialSettings.observationPeriod);
    const [goalAmount, setGoalAmount] = useState(initialSettings.goalAmount);
    const [forecastAmount, setForecastAmount] = useState(initialSettings.forecastAmount);
    const [workingDaysTotal, setWorkingDaysTotal] = useState(initialSettings.workingDaysTotal);
    const [workingDaysRemaining, setWorkingDaysRemaining] = useState(initialSettings.workingDaysRemaining);

    // Calculate working days based on observation period
    // Assuming 5 working days per week (Monday-Friday)
    const calculateWorkingDays = (days) => {
        const weeks = Math.floor(days / 7);
        const remainingDays = days % 7;
        
        // 5 working days per week
        let workingDays = weeks * 5;
        
        // Add remaining days (assuming they're not all weekends)
        // Simple approximation: 5/7 of remaining days are working days
        workingDays += Math.round(remainingDays * (5/7));
        
        return workingDays;
    };

    // Update working days when observation period changes
    useEffect(() => {
        const totalDays = calculateWorkingDays(observationPeriod);
        setWorkingDaysTotal(totalDays);
        setWorkingDaysRemaining(totalDays); // Initially all days are remaining
    }, [observationPeriod]);

    // Save to localStorage whenever any setting changes
    useEffect(() => {
        try {
            const settings = {
                productionGoal,
                observationPeriod,
                goalAmount,
                forecastAmount,
                workingDaysTotal,
                workingDaysRemaining
            };
            localStorage.setItem('sessionSettings', JSON.stringify(settings));
        } catch (error) {
            console.error('Error saving sessionSettings to localStorage:', error);
        }
    }, [productionGoal, observationPeriod, goalAmount, forecastAmount, workingDaysTotal, workingDaysRemaining]);

    // Calculate goal amount based on current production and growth percentage
    const updateGoalFromPercentage = (percentage, currentProduction = 121480) => {
        const newGoal = Math.round(currentProduction * (1 + percentage / 100));
        setGoalAmount(newGoal);
        setProductionGoal(percentage);
    };

    // Calculate forecast based on goal and current trends
    const updateForecast = (goal) => {
        // Simple calculation: forecast is typically 95-98% of goal
        const forecast = Math.round(goal * 0.98);
        setForecastAmount(forecast);
    };

    // Update remaining working days (can be called as days pass)
    const updateRemainingDays = (daysElapsed) => {
        const workingDaysElapsed = calculateWorkingDays(daysElapsed);
        const remaining = Math.max(0, workingDaysTotal - workingDaysElapsed);
        setWorkingDaysRemaining(remaining);
    };

    // Clear all settings (called when SDP mode is turned off)
    const clearSettings = () => {
        setProductionGoal(5);
        setObservationPeriod(30);
        setGoalAmount(100000);
        setForecastAmount(98000);
        setWorkingDaysTotal(22);
        setWorkingDaysRemaining(22);
        try {
            localStorage.removeItem('sessionSettings');
        } catch (error) {
            console.error('Error clearing sessionSettings from localStorage:', error);
        }
    };

    return (
        <SessionSettingsContext.Provider
            value={{
                productionGoal,
                setProductionGoal,
                observationPeriod,
                setObservationPeriod,
                goalAmount,
                setGoalAmount,
                forecastAmount,
                setForecastAmount,
                workingDaysTotal,
                workingDaysRemaining,
                updateGoalFromPercentage,
                updateForecast,
                updateRemainingDays,
                clearSettings
            }}
        >
            {children}
        </SessionSettingsContext.Provider>
    );
};
